import React, { useEffect, useState } from 'react';
import { Flag, X } from 'lucide-react';
import { REPORT_REASONS, reportContent } from '../../utils/moderation';
import './ReportModal.css';

const TARGET_LABELS = { post: 'post', reply: 'comment', user: 'member' };

/**
 * Report a post, comment (reply), or member to the admins.
 * target: { type: 'post' | 'reply' | 'user', id, name? }
 */
export default function ReportModal({ open, target, user, onClose, onReported }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setDetails('');
      setError('');
      setDone('');
      setSubmitting(false);
    }
  }, [open, target?.type, target?.id]);

  if (!open || !target) return null;

  const label = TARGET_LABELS[target.type] || 'content';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) {
      setError('Please choose a reason.');
      return;
    }
    setSubmitting(true);
    setError('');
    const result = await reportContent(user?.id, target.type, target.id, reason, details.trim());
    setSubmitting(false);
    if (result.success) {
      setDone(result.message || 'Thanks — an admin will review this.');
      onReported?.(target);
    } else {
      setError(result.message || 'Could not submit the report.');
    }
  };

  const close = () => {
    if (!submitting) onClose?.();
  };

  return (
    <div className="rm-backdrop" onClick={close}>
      <div
        className="rm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rm-header">
          <span className="rm-header-icon" aria-hidden="true">
            <Flag size={18} strokeWidth={2.2} />
          </span>
          <h3 id="rm-title" className="rm-title">
            Report {label}
            {target.name ? <span className="rm-subtitle"> · {target.name}</span> : null}
          </h3>
          <button type="button" className="rm-close" onClick={close} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="rm-body">
            <p className="rm-done">{done}</p>
            <div className="rm-actions">
              <button type="button" className="rm-btn rm-btn--primary" onClick={close}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <form className="rm-body" onSubmit={handleSubmit}>
            <p className="rm-help">
              Why are you reporting this {label}? Reports are private — the {label === 'member' ? 'member' : 'author'} won't see who reported them.
            </p>

            <div className="rm-reasons" role="radiogroup" aria-label="Reason">
              {REPORT_REASONS.map((r) => (
                <label
                  key={r.id}
                  className={`rm-reason${reason === r.id ? ' rm-reason--active' : ''}`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.id}
                    checked={reason === r.id}
                    onChange={() => setReason(r.id)}
                  />
                  {r.label}
                </label>
              ))}
            </div>

            <label className="rm-details-label" htmlFor="rm-details">
              Anything else admins should know? <span className="rm-optional">(optional)</span>
            </label>
            <textarea
              id="rm-details"
              className="rm-details"
              rows={3}
              maxLength={1000}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />

            {error && <p className="rm-error">{error}</p>}

            <div className="rm-actions">
              <button type="button" className="rm-btn" onClick={close} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="rm-btn rm-btn--danger" disabled={submitting}>
                {submitting ? 'Sending…' : 'Submit report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
